import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../../utils/cms.utils";
import ContentTag from "../../../utils/tag";
import { getAgeRangePublicRoute } from "../../../utils/ageRange.util";
import { FONT_FAMILY } from "../../../config/globalStyles";
import { FOCUS_BORDER } from "../../../config/focusStyles";
import { useInputMode } from "../../../utils/hooks/useInputMode";
import OptimizedImage from "../../common/OptimizedImage";

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

var ContentRowWrapper = styled.div`
  position: relative;
`;

var RowContainer = styled.div`
  display: flex;
  width: 100%;
`;

var ItemWrapper = styled.div`
  width: 20%;
  min-width: 160px;
  padding: 12px;
  box-sizing: border-box;
`;

var ItemContainer = styled.div`
  width: 100%;
  position: relative;
  padding-top: 140%;
`;

var ItemCard = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: ${props => props.$focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive};
  overflow: hidden;
  z-index: ${props => props.$focused ? 10 : 0};
  border-radius: 16px;
  cursor: pointer;
  -webkit-transition: border 0.2s, transform 0.2s;
  transition: border 0.2s, transform 0.2s;
  transform: ${props => props.$focused ? "scale(1.02)" : "scale(1)"};
`;

var FocusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 16px;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
  border-radius: inherit;
`;

var TitleText = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: ${FONT_FAMILY};
`;

var YearText = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  font-family: ${FONT_FAMILY};
`;

// ============================================================================
// COMPONENTS
// ============================================================================

const MovieItem = ({ item = {}, onEnterPress, onFocus }) => {
  const { contentPhotos, slug, tags } = item;
  const { focused, ref, focusSelf } = useFocusable({
    onEnterPress,
    onFocus,
    focusKey: "content_" + slug,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <ItemWrapper>
      <ItemContainer>
        <ItemCard
          ref={ref}
          $focused={focused}
          onMouseEnter={handleMouseEnter}
          onClick={onEnterPress}
        >
          <OptimizedImage
            src={imageData.src}
            alt={item.title}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit',
            }}
          />
          {tags?.[0] && <ContentTag label={tags?.[0]} />}
          <FocusOverlay $visible={focused}>
            <TitleText>{item.title}</TitleText>
            <YearText>{item.year}</YearText>
          </FocusOverlay>
        </ItemCard>
      </ItemContainer>
    </ItemWrapper>
  );
};

/**
 * Single row of movie items with its own focus context
 */
const MovieRow = ({ items, onFocus, navigate }) => {
  const { ref, focusKey } = useFocusable({ onFocus });

  return (
    <FocusContext.Provider value={focusKey}>
      <RowContainer ref={ref}>
        {items.map((item, index) => (
          <MovieItem
            item={item}
            key={index}
            onFocus={onFocus}
            onEnterPress={() =>
              navigate(
                `/${item.contentType?.kind || item.kind || "movie"}/${item.slug}`
              )
            }
          />
        ))}
      </RowContainer>
    </FocusContext.Provider>
  );
};

export const MoviesSlider = ({ movies, onFocus }) => {
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter((x) => x.ageRange <= ageRange);
  const { ref, focusKey } = useFocusable();

  const navigate = useNavigate();

  // Chunk items into rows
  const chunkedItems = useMemo(
    function () {
      if (!_items || _items.length === 0) return [];
      return chunkArray(_items, ITEMS_PER_ROW);
    },
    [_items]
  );

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref}>
        {chunkedItems.map((rowItems, rowIndex) => (
          <MovieRow
            key={"row_" + rowIndex}
            items={rowItems}
            onFocus={onFocus}
            navigate={navigate}
          />
        ))}
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
